// Lógica PURA da navegação por abas internas do admin (sem React/router), para ser testável de forma
// determinística. Identidade de uma aba = `pathname` (inclui params de rota como :id; EXCLUI a query
// string — filtros são estado da aba, não criam abas novas).

export const MAX_TABS = 7;

export type TabLocation = {
  pathname: string;
  search: string;
};

export type AdminTab = {
  key: string;
  location: TabLocation;
  title: string;
};

export type SyncResult =
  | { type: "activated"; key: string }
  | { type: "opened"; key: string }
  | { type: "updated"; key: string }
  | { type: "blocked"; revertTo: TabLocation };

export type CloseResult = {
  tabs: AdminTab[];
  activeKey: string | null;
  nextLocation: TabLocation | null;
  wentHome: boolean;
};

/** Identidade da aba: pathname sem barra final (params preservados, query string ignorada). */
export function tabKeyOf(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed.length > 0 ? trimmed : "/";
}

/** Caminhos que NÃO viram aba mesmo tendo segmento (são a "home" renderizada sem aba). */
const NON_TABBABLE = new Set(["/admin/boas-vindas"]);

/**
 * Caminhos que viram aba: têm segmento após /admin ou /dashboard. `/admin` puro (redirect) não vira aba, e
 * a tela inicial de boas-vindas (`/admin/boas-vindas`) é a home — renderizada sem chip de aba.
 */
export function isTabbablePath(pathname: string): boolean {
  if (NON_TABBABLE.has(tabKeyOf(pathname))) return false;
  return /^\/(admin|dashboard)\/.+/.test(pathname);
}

/**
 * Reconcilia as abas com uma nova localização:
 * - mesma aba (mesmo pathname) → ativa e atualiza a search (filtros são estado da aba);
 * - aba nova abaixo do limite → abre e ativa;
 * - aba nova no limite de 5 → BLOQUEIA (nunca substitui/fecha automaticamente) e indica reverter a URL
 *   para a aba atual, mantendo a navegação coerente.
 */
export function syncTabs(
  tabs: AdminTab[],
  loc: TabLocation,
  titleFor: (loc: TabLocation) => string,
  activeKey: string | null,
): { tabs: AdminTab[]; activeKey: string | null; result: SyncResult } {
  const key = tabKeyOf(loc.pathname);
  const idx = tabs.findIndex((t) => t.key === key);

  if (idx >= 0) {
    const updated = tabs.map((t, i) => (i === idx ? { ...t, location: loc } : t));
    const type = activeKey === key ? "updated" : "activated";
    return { tabs: updated, activeKey: key, result: { type, key } };
  }

  if (tabs.length >= MAX_TABS) {
    const active = tabs.find((t) => t.key === activeKey) ?? tabs[tabs.length - 1];
    return { tabs, activeKey, result: { type: "blocked", revertTo: active ? active.location : loc } };
  }

  const tab: AdminTab = { key, location: loc, title: titleFor(loc) };
  return { tabs: [...tabs, tab], activeKey: key, result: { type: "opened", key } };
}

/**
 * Fecha uma aba. Ao fechar a ATIVA, seleciona a vizinha à direita; na ausência, a da esquerda. Fechar uma
 * aba inativa não mexe na seleção. Fechar a última sinaliza `wentHome` (a UI navega para a home do papel).
 */
export function closeTab(tabs: AdminTab[], activeKey: string | null, key: string): CloseResult {
  const idx = tabs.findIndex((t) => t.key === key);
  if (idx < 0) return { tabs, activeKey, nextLocation: null, wentHome: false };

  const remaining = tabs.filter((t) => t.key !== key);

  if (activeKey !== key) {
    return { tabs: remaining, activeKey, nextLocation: null, wentHome: false };
  }
  if (remaining.length === 0) {
    return { tabs: remaining, activeKey: null, nextLocation: null, wentHome: true };
  }
  const nextIdx = idx < remaining.length ? idx : idx - 1;
  const next = remaining[nextIdx];
  return { tabs: remaining, activeKey: next.key, nextLocation: next.location, wentHome: false };
}
